"use client";

import { useState, useRef } from "react";
import { Container, SectionTitle } from "./Primitives";
import type { CompanyService } from "@/lib/companySettings";

/**
 * Services — двухколоночный блок «Наши услуги».
 *
 * Данные ТОЛЬКО из админки (раздел «Автопилот компания» → «Наши услуги»).
 * Если список пуст — секция не рендерится. Никаких захардкоженных дефолтов,
 * чтобы было видно, что данные реально приходят из БД.
 *
 *  • LEFT: текстовые карточки. Активная — синяя `bg-primary` с body,
 *    вылезает вправо и заходит под картинку. Неактивные — белые.
 *  • RIGHT: общая картинка, активный slide z-[2], previous z-[1] для
 *    плавного fade-in поверх старого изображения.
 *
 * Мобильно: картинка сверху, аккордеон под ней.
 */
export default function Services({
  services,
}: {
  services?: CompanyService[];
} = {}) {
  const list = services ?? [];

  // Hooks вызываем до раннего return, чтобы порядок не менялся между
  // рендерами (react/hooks rule).
  const [activeIdx, setActiveIdx] = useState(0);
  const [previousIdx, setPreviousIdx] = useState<number | null>(null);
  const prevTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (list.length === 0) return null;

  // Защита от outOfBounds, если активный индекс остался от прежнего стейта,
  // а список схлопнулся в админке. Visible/safe activeIdx без записи в state.
  const activeIdxSafe = Math.min(activeIdx, list.length - 1);

  function selectService(i: number) {
    if (i === activeIdxSafe) return;
    if (prevTimerRef.current) clearTimeout(prevTimerRef.current);
    setPreviousIdx(activeIdxSafe);
    setActiveIdx(i);
    prevTimerRef.current = setTimeout(() => setPreviousIdx(null), 250);
  }

  return (
    <section id="services" className="pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]">
      <Container>
        <SectionTitle className="mb-8 sm:mb-10 lg:mb-[50px]">Наши услуги</SectionTitle>

        {/* lg:items-stretch — растягивает обе колонки на одинаковую высоту,
            чтобы картинка совпадала с высотой аккордеона. min-h убрана:
            высота диктуется содержимым левой колонки (раскрытая активная
            карточка задаёт «потолок»). */}
        <div className="relative grid gap-5 lg:grid-cols-[1fr_minmax(360px,460px)] lg:gap-8 xl:gap-10 lg:items-stretch">
          <ul className="relative isolate z-[1] flex flex-col gap-2 sm:gap-3 lg:order-1">
            {list.map((s, i) => {
              const isActive = i === activeIdxSafe;
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => selectService(i)}
                    aria-expanded={isActive}
                    style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)", WebkitAppearance: "none", appearance: "none" }}
                    className={`block w-full appearance-none border-0 p-0 m-0 text-left rounded-[20px] sm:rounded-[24px] ${
                      isActive
                        ? "transition-[width,background-color,box-shadow] duration-150 bg-primary lg:w-[calc(100%+92px)] xl:w-[calc(100%+120px)] shadow-[0_24px_64px_-12px_rgba(0,51,161,0.5)]"
                        : "bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)]"
                    }`}
                  >
                    {/* Неактивные — компактнее. gap-0 у неактивной, чтобы
                        flex-gap не оставлял место под схлопнутой body-плашкой
                        (это и был «лишний» отступ снизу). У активной — gap
                        между title и body. */}
                    <div
                      className={`flex flex-col pl-6 sm:pl-8 lg:pl-10 xl:pl-[44px] ${
                        isActive
                          ? "gap-4 sm:gap-5 py-4 sm:py-5 lg:py-6 pr-6 sm:pr-8 lg:pr-[120px] xl:pr-[160px]"
                          : "gap-0 py-4 sm:py-5 lg:py-6 pr-6 sm:pr-8 lg:pr-10 xl:pr-[44px]"
                      }`}
                    >
                      <h3
                        className={`text-[16px] font-medium leading-[1.3] tracking-[-0.3px] transition-colors duration-300 sm:text-[18px] lg:text-[20px] xl:text-[22px] xl:leading-[1.3] ${
                          isActive ? "text-white" : "text-text"
                        }`}
                      >
                        {s.title}
                      </h3>
                      <div
                        className={`overflow-hidden ${
                          isActive
                            ? "transition-[max-height,opacity] duration-200 ease-out max-h-[600px] opacity-100 mt-3 sm:mt-4 lg:mt-5"
                            : "max-h-0 opacity-0 mt-0"
                        }`}
                      >
                        <p className="text-[14px] leading-[1.55] tracking-[-0.2px] text-on-blue sm:text-[15px] lg:text-[16px] xl:text-[17px]">
                          {s.body}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* RIGHT image stack. Используем <img>, чтобы /uploads/* работало
              без remotePatterns конфига для Next/Image. */}
          {/* Высота на мобайле фиксированная (compact), на десктопе колонка
              сама подгоняется под высоту аккордеона благодаря lg:items-stretch
              на родителе и h-full здесь. */}
          <div className="relative z-10 h-64 overflow-hidden rounded-[24px] sm:h-80 sm:rounded-[30px] lg:order-2 lg:h-full">
            {list.map((s, i) => {
              const isActive = i === activeIdxSafe;
              const isPrevious = i === previousIdx;
              return s.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={i}
                  src={s.image}
                  alt=""
                  className={`absolute inset-0 size-full object-cover ${
                    isActive
                      ? "z-[2] opacity-100 transition-opacity duration-200 ease-out"
                      : isPrevious
                      ? "z-[1] opacity-100"
                      : "z-0 opacity-0"
                  }`}
                />
              ) : null;
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}
